/**
 * URL Insert Menu - URL 단독 붙여넣기 시 삽입 방식 선택 (노션식)
 *
 * paragraph에 URL을 단독으로 붙여넣으면 작은 플로팅 팝업을 띄워
 * 삽입 방식을 고르게 한다:
 *   - "URL 그대로 두기"   (기본) — 붙여넣은 텍스트/인라인 링크 유지
 *   - "링크 카드로 표시"   — 서버 메타 파싱 후 LinkTool 카드 블록 추가
 *   - "임베드로 표시"      — 임베드 가능한 서비스(youtube 등)일 때만 노출, Embed 블록 추가
 *
 * 이미 내용이 있는 줄에 URL만 붙여넣은 경우에도 팝업이 뜨며, 카드/임베드를 고르면
 * 기존 텍스트는 그대로 두고 URL만 제거한 뒤 아래 줄에 블록을 추가한다.
 *
 * 아무것도 고르지 않고 타이핑하거나 바깥을 클릭하면 팝업은 닫히고 URL은 그대로 남는다.
 */

import api from '@/utils/api';
import Embed from '@/editorjs/tools/embed/index';

// LinkTool 메타 파싱 엔드포인트 (editor_core.js linkTool config와 동일)
const LINK_PARSE_ENDPOINT = '/api/links/parse';

const MENU_CLASS = 'maily-url-insert-menu';

// 스킴이 있는 완전한 URL
const SCHEME_URL = /^https?:\/\/\S+$/i;
// 스킴 없는 도메인(+선택적 path/query/hash). tld 2~24 alpha.
//   예) www.naver.com, example.com/a/b, t.me/durov/43
const BARE_DOMAIN = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,24}(?:[/?#]\S*)?$/i;

/**
 * 붙여넣은 텍스트가 "단독 URL"인지 판별.
 * 앞뒤 공백을 제외하고 전체가 하나의 URL 토큰이어야 한다(내부 공백 불가).
 * 문장 안에 URL이 섞여 들어온 경우는 false → 기존대로 인라인 링크 텍스트 유지.
 *
 * @param {string} text
 * @returns {boolean}
 */
export function isStandaloneUrl(text) {
    if (!text || typeof text !== 'string') return false;

    const trimmed = text.trim();
    if (!trimmed || /\s/.test(trimmed)) return false;

    // 스킴 없는 이메일 형태(foo@bar.com)는 제외
    if (trimmed.includes('@') && !SCHEME_URL.test(trimmed)) return false;

    return SCHEME_URL.test(trimmed) || BARE_DOMAIN.test(trimmed);
}

/**
 * 스킴이 없으면 https:// 를 보강. (LinkTool/MetaService 입력 및 embed 매칭용)
 *
 * @param {string} text
 * @returns {string}
 */
export function normalizeUrl(text) {
    const trimmed = (text || '').trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
}

/**
 * URL과 매칭되는 Embed 서비스를 찾는다. (Embed.services는 prepare()에서 채워짐)
 *
 * @param {string} url
 * @returns {{service: string, cfg: object}|null}
 */
export function findEmbedService(url) {
    const services = Embed.services || {};

    for (const [service, cfg] of Object.entries(services)) {
        if (cfg && cfg.regex instanceof RegExp) {
            cfg.regex.lastIndex = 0; // 글로벌 플래그 대비 방어
            if (cfg.regex.test(url)) {
                return { service, cfg };
            }
        }
    }

    return null;
}

/**
 * Embed 블록 데이터 생성. embed/index.js의 onPaste(라인 213-227)와 동일 로직.
 *
 * @param {string} url
 * @param {string} service
 * @param {object} cfg - Embed.services[service]
 * @returns {{service: string, source: string, embed: string, width: number, height: number}}
 */
export function buildEmbedData(url, service, cfg) {
    const { regex, embedUrl, width, height, id = (ids) => ids.shift() } = cfg;

    regex.lastIndex = 0;
    const result = regex.exec(url).slice(1);
    const embed = embedUrl.replace(/<%= remote_id %>/g, id(result));

    return { service, source: url, embed, width, height };
}

// ───────────────────────────── 내부 헬퍼 ─────────────────────────────

let _activeMenu = null;

function _closeActiveMenu() {
    if (_activeMenu) {
        _activeMenu.close();
        _activeMenu = null;
    }
}

function _isDark() {
    return document.documentElement.classList.contains('dark');
}

/**
 * 콜백을 undo 히스토리 일시 중지 상태에서 실행하고 끝나면 registerChange.
 * (paragraph_transformer.js 변환 패턴과 동일. 컬럼 내부면 skipUndo)
 */
async function _undoWrap(editor, skipUndo, fn) {
    const undo = skipUndo ? null : editor.undo;
    const originalShouldSaveHistory = undo?.shouldSaveHistory;
    if (undo) undo.shouldSaveHistory = false;

    try {
        await fn();
    } catch (error) {
        console.error('[UrlInsertMenu] 작업 실패:', error);
    } finally {
        if (undo) {
            undo.shouldSaveHistory = originalShouldSaveHistory;
            requestAnimationFrame(() => {
                if (undo.registerChange) undo.registerChange();
            });
        }
    }
}

/** paragraph 블록의 텍스트(HTML)를 교체 */
async function _setParagraphHtml(editor, skipUndo, blockId, html) {
    await _undoWrap(editor, skipUndo, () => editor.blocks.update(blockId, { text: html }));
}

/**
 * 변환된 블록(linkTool/embed) 삽입.
 * - wasEmpty(URL만 있던 빈 줄): 그 줄을 새 블록으로 교체 + 트레일링 paragraph
 * - 그 외(기존 텍스트 존재): (필요 시 URL 제거 후) 아래 줄에 새 블록 추가 + 트레일링 paragraph
 *
 * @param {boolean} alreadyStripped - 카드 로딩 시 이미 URL을 제거한 경우 true
 */
async function _insertConverted(editor, skipUndo, { blockId, type, data, wasEmpty, originalHtml, alreadyStripped = false }) {
    await _undoWrap(editor, skipUndo, async () => {
        let idx = editor.blocks.getBlockIndex(blockId);
        if (idx == null || idx < 0) return;

        if (wasEmpty) {
            // 빈 paragraph를 새 블록으로 교체 (replace=true)
            await editor.blocks.insert(type, data, undefined, idx, true, true);
            await editor.blocks.insert('paragraph', { text: '' }, undefined, idx + 1, true);
            try { editor.caret.setToBlock(idx + 1, 'start'); } catch (e) { /* 무시 */ }
        } else {
            // 기존 텍스트는 두고 URL만 제거
            if (!alreadyStripped) {
                await editor.blocks.update(blockId, { text: originalHtml });
                idx = editor.blocks.getBlockIndex(blockId);
                if (idx == null || idx < 0) return;
            }
            await editor.blocks.insert(type, data, undefined, idx + 1, true);
            await editor.blocks.insert('paragraph', { text: '' }, undefined, idx + 2, true);
            try { editor.caret.setToBlock(idx + 2, 'start'); } catch (e) { /* 무시 */ }
        }
    });
}

/**
 * 플로팅 팝업을 띄운다.
 *
 * @param {object} options
 * @param {EditorJS} options.editor       - EditorJS 인스턴스
 * @param {string}   options.url          - 정규화된 URL (https:// 보강됨)
 * @param {string}   options.blockId      - 붙여넣은 URL이 들어간 paragraph 블록 id
 * @param {DOMRect}  options.anchorRect    - 팝업 기준 위치 (붙여넣은 텍스트의 rect)
 * @param {boolean}  options.skipUndo     - 컬럼 내부 에디터 여부
 * @param {string}   options.originalHtml - 붙여넣기 전 paragraph innerHTML (URL 제거 후 복원용)
 * @param {string}   options.pastedHtml   - 붙여넣기 후 paragraph innerHTML (실패 시 URL 복원용)
 * @param {boolean}  options.wasEmpty     - 붙여넣기 전 paragraph가 비어있었는지
 */
export function showUrlInsertMenu({ editor, url, blockId, anchorRect, skipUndo = false, originalHtml = '', pastedHtml = '', wasEmpty = true }) {
    _closeActiveMenu();

    const embedMatch = findEmbedService(url);

    const options = [
        { key: 'raw', label: 'URL 그대로 두기' },
        { key: 'card', label: '링크 카드로 표시' },
    ];
    if (embedMatch) {
        options.push({ key: 'embed', label: '임베드로 표시' });
    }

    const dark = _isDark();
    const colors = dark
        ? { bg: '#1f2937', border: '#374151', text: '#e5e7eb', hint: '#9ca3af', active: '#374151', hintBorder: '#374151' }
        : { bg: '#ffffff', border: '#e2e8f0', text: '#1f2937', hint: '#94a3b8', active: '#f1f5f9', hintBorder: '#eef2f7' };

    // 컨테이너
    const menu = document.createElement('div');
    menu.className = MENU_CLASS;
    menu.style.cssText = [
        'position: fixed',
        'z-index: 1000',
        'min-width: 200px',
        `background: ${colors.bg}`,
        `border: 1px solid ${colors.border}`,
        'border-radius: 8px',
        'box-shadow: 0 6px 20px rgba(0,0,0,0.12)',
        'padding: 4px',
        'font-size: 13px',
        `color: ${colors.text}`,
        'user-select: none',
    ].join(';');

    let busy = false;
    let closed = false;
    let activeIndex = 0;
    const rows = [];

    const renderActive = () => {
        rows.forEach((row, i) => {
            row.style.background = i === activeIndex ? colors.active : 'transparent';
        });
    };

    const close = () => {
        if (closed) return;
        closed = true;
        document.removeEventListener('keydown', onKeydown, true);
        document.removeEventListener('mousedown', onDocMouseDown, true);
        window.removeEventListener('scroll', onViewportChange, true);
        window.removeEventListener('resize', onViewportChange);
        if (menu.parentNode) menu.parentNode.removeChild(menu);
        if (_activeMenu && _activeMenu.el === menu) _activeMenu = null;
    };

    const setBusy = (message) => {
        busy = true;
        menu.innerHTML = '';
        const loading = document.createElement('div');
        loading.style.cssText = `display:flex; align-items:center; gap:8px; padding: 10px 12px; color: ${colors.hint};`;

        const spinner = document.createElement('span');
        spinner.style.cssText = [
            'width:14px', 'height:14px', 'border-radius:50%',
            `border:2px solid ${colors.border}`, `border-top-color:${colors.text}`,
            'display:inline-block', 'animation: maily-url-spin 0.7s linear infinite',
        ].join(';');
        _ensureSpinnerKeyframes();

        const text = document.createElement('span');
        text.textContent = message;

        loading.appendChild(spinner);
        loading.appendChild(text);
        menu.appendChild(loading);
    };

    const showError = (message) => {
        busy = false;
        if (closed) return;
        menu.innerHTML = '';
        const err = document.createElement('div');
        err.style.cssText = 'padding: 10px 12px; color: #ef4444; max-width: 240px; line-height: 1.4;';
        err.textContent = `${message} URL은 그대로 유지됩니다.`;
        menu.appendChild(err);
        setTimeout(close, 1800);
    };

    const select = async (index) => {
        if (busy) return;
        const opt = options[index];
        if (!opt) return;

        if (opt.key === 'raw') {
            close();
            return;
        }

        if (opt.key === 'embed') {
            if (!embedMatch) {
                close();
                return;
            }

            const data = buildEmbedData(url, embedMatch.service, embedMatch.cfg);

            // 텔레그램: 이메일에서 보여줄 미리보기(제목/설명/이미지)를 미리 파싱해 임베드 데이터에 저장.
            // 웹은 위젯으로 실시간 표시되고, 이메일은 저장된 meta로 카드를 렌더한다.
            if (embedMatch.service === 'telegram') {
                setBusy('미리보기를 불러오는 중…');
                await _setParagraphHtml(editor, skipUndo, blockId, originalHtml); // 로딩 중 URL 숨김
                try {
                    const response = await api.post(LINK_PARSE_ENDPOINT, { url });
                    const body = response?.data;
                    if (body && body.success && body.meta) {
                        data.meta = body.meta;
                    }
                } catch (error) {
                    // 메타 파싱 실패해도 임베드는 진행 (이메일은 기본 카드로 폴백)
                    console.error('[UrlInsertMenu] 텔레그램 미리보기 파싱 실패:', error);
                }
                await _insertConverted(editor, skipUndo, {
                    blockId, type: 'embed', data, wasEmpty, originalHtml, alreadyStripped: true,
                });
                close();
                return;
            }

            close();
            _insertConverted(editor, skipUndo, { blockId, type: 'embed', data, wasEmpty, originalHtml });
            return;
        }

        if (opt.key === 'card') {
            setBusy('링크 정보를 불러오는 중…');
            // 로딩 시작과 함께 URL 숨김 (낙관적). 기존 텍스트가 있으면 그 텍스트만 남는다.
            await _setParagraphHtml(editor, skipUndo, blockId, originalHtml);

            try {
                const response = await api.post(LINK_PARSE_ENDPOINT, { url });
                const body = response?.data;
                if (body && body.success && body.meta) {
                    await _insertConverted(editor, skipUndo, {
                        blockId,
                        type: 'linkTool',
                        data: { link: body.link || url, meta: body.meta },
                        wasEmpty,
                        originalHtml,
                        alreadyStripped: true,
                    });
                    close();
                } else {
                    // 실패 → URL 복원
                    await _setParagraphHtml(editor, skipUndo, blockId, pastedHtml);
                    showError((body && body.msg) || '링크 정보를 가져오지 못했습니다.');
                }
            } catch (error) {
                console.error('[UrlInsertMenu] 링크 메타 파싱 실패:', error);
                await _setParagraphHtml(editor, skipUndo, blockId, pastedHtml);
                showError('링크 정보를 가져오지 못했습니다.');
            }
        }
    };

    // 팝업이 처리하는 키는 에디터로 전파되지 않도록 완전히 차단
    const block = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
    };

    function onKeydown(e) {
        // 단독 수정자 키는 무시 (팝업 유지)
        if (['Shift', 'Control', 'Alt', 'Meta'].includes(e.key)) return;

        // 위/아래는 항상 팝업이 가로채 에디터 블록/캐럿 이동을 막는다 (옵션 내에서만 이동)
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            block(e);
            if (busy) return;
            activeIndex = e.key === 'ArrowDown'
                ? (activeIndex + 1) % options.length
                : (activeIndex - 1 + options.length) % options.length;
            renderActive();
            return;
        }

        // 로딩 중에는 그 외 키 무시 (팝업 유지)
        if (busy) return;

        switch (e.key) {
            case 'Enter':
                block(e);
                select(activeIndex);
                break;
            case 'Escape':
                block(e);
                close();
                break;
            default:
                // 스페이스/텍스트 등 → 팝업 닫고 입력은 에디터로 그대로 통과
                close();
        }
    }

    function onDocMouseDown(e) {
        if (busy) return;
        if (!menu.contains(e.target)) close();
    }

    function onViewportChange() {
        if (busy) return;
        close();
    }

    // 옵션 행 생성
    options.forEach((opt, i) => {
        const row = document.createElement('div');
        row.style.cssText = [
            'display: flex',
            'align-items: center',
            'gap: 8px',
            'padding: 8px 10px',
            'border-radius: 6px',
            'cursor: pointer',
            'white-space: nowrap',
        ].join(';');

        const check = document.createElement('span');
        check.style.cssText = `width: 14px; display: inline-block; color: ${colors.hint};`;
        check.textContent = opt.key === 'raw' ? '✓' : '';

        const label = document.createElement('span');
        label.textContent = opt.label;

        row.appendChild(check);
        row.appendChild(label);

        row.addEventListener('mouseenter', () => { activeIndex = i; renderActive(); });
        // 클릭 시 에디터 포커스/선택이 풀리지 않도록 mousedown 기본동작 차단
        row.addEventListener('mousedown', (e) => e.preventDefault());
        row.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            select(i);
        });

        rows.push(row);
        menu.appendChild(row);
    });

    // 힌트 푸터
    const hint = document.createElement('div');
    hint.style.cssText = `margin-top: 4px; padding: 6px 10px; border-top: 1px solid ${colors.hintBorder}; color: ${colors.hint}; font-size: 11px;`;
    hint.textContent = 'Enter 선택 · Esc 닫기';
    menu.appendChild(hint);

    renderActive();

    // 위치 계산 (뷰포트 클램프)
    document.body.appendChild(menu);
    const menuRect = menu.getBoundingClientRect();
    const margin = 6;
    let top = anchorRect.bottom + margin;
    let left = anchorRect.left;

    if (left + menuRect.width > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - menuRect.width - 8);
    }
    if (top + menuRect.height > window.innerHeight - 8) {
        // 아래 공간이 부족하면 위쪽에 배치
        top = Math.max(8, anchorRect.top - menuRect.height - margin);
    }

    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;

    // dismiss 리스너 등록 (capture로 에디터보다 먼저)
    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('mousedown', onDocMouseDown, true);
    window.addEventListener('scroll', onViewportChange, true);
    window.addEventListener('resize', onViewportChange);

    _activeMenu = { el: menu, close };
}

let _spinnerKeyframesAdded = false;
function _ensureSpinnerKeyframes() {
    if (_spinnerKeyframesAdded) return;
    _spinnerKeyframesAdded = true;
    const style = document.createElement('style');
    style.textContent = '@keyframes maily-url-spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
}

export default { showUrlInsertMenu, isStandaloneUrl, normalizeUrl, findEmbedService, buildEmbedData };
